"use client";

import { c as _c } from "react/compiler-runtime";
import { Check, ChevronsUpDown } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../lib/cn";
export type ComboboxItem = {
  value: string;
  label: string;
  description?: string;
  /** Extra search terms, not rendered. Lets a renamed item stay findable by its old name. */
  keywords?: readonly string[];
  icon?: ReactNode;
  isGroupHeader?: boolean;
};

/** Single-select. value accepts string[] so a multi-valued caller state can render as "Multiple selected". */
export type ComboboxProps = {
  id?: string;
  items: ComboboxItem[];
  value?: string | string[] | undefined;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  popoverContentClassName?: string;
  allowCustom?: boolean;
  /**
   * Re-selecting the only selected item reports "" instead of the value. For
   * filters, where "nothing selected" is valid. Off by default, so a required
   * field cannot be emptied by a stray click.
   */
  allowDeselect?: boolean;
  disabled?: boolean;
};
function selectedSet(value: string | string[] | undefined): Set<string> {
  if (value === undefined || value === "") {
    return new Set();
  }
  return new Set(Array.isArray(value) ? value : [value]);
}
export function Combobox(t0) {
  const $ = _c(69);
  const {
    id,
    items,
    value,
    onValueChange,
    placeholder: t1,
    searchPlaceholder: t2,
    emptyMessage: t3,
    className,
    popoverContentClassName,
    allowCustom: t4,
    allowDeselect: t5,
    disabled: t6
  } = t0;
  const placeholder = t1 === undefined ? "Select an item..." : t1;
  const searchPlaceholder = t2 === undefined ? "Search..." : t2;
  const emptyMessage = t3 === undefined ? "No options found." : t3;
  const allowCustom = t4 === undefined ? false : t4;
  const allowDeselect = t5 === undefined ? false : t5;
  const disabled = t6 === undefined ? false : t6;
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t7;
  let t8;
  let t9;
  if ($[0] !== allowCustom || $[1] !== allowDeselect || $[2] !== className || $[3] !== disabled || $[4] !== emptyMessage || $[5] !== id || $[6] !== inputValue || $[7] !== items || $[8] !== onValueChange || $[9] !== open || $[10] !== placeholder || $[11] !== popoverContentClassName || $[12] !== searchPlaceholder || $[13] !== value) {
    const selected = selectedSet(value);
    const handleSelect = currentValue => {
      if (onValueChange !== undefined) {
        const isOnlySelection = allowDeselect && selected.size === 1 && selected.has(currentValue);
        onValueChange(isOnlySelection ? "" : currentValue);
      }
      setInputValue("");
      setOpen(false);
    };
    const normalizedInput = inputValue.trim();
    const search = normalizedInput.toLowerCase();
    const filteredItems = items.filter(item => item.label.toLowerCase().includes(search) || item.value.toLowerCase().includes(search) || (item.description?.toLowerCase().includes(search) ?? false) || (item.keywords?.some(keyword => keyword.toLowerCase().includes(search)) ?? false));
    const exactMatch = allowCustom && normalizedInput !== "" ? filteredItems.find(item_0 => item_0.label.toLowerCase() === search || item_0.value.toLowerCase() === search) : undefined;
    const orderedItems = allowCustom && exactMatch !== undefined ? [exactMatch, ...filteredItems.filter(item_1 => item_1 !== exactMatch)] : filteredItems;
    const customItem = allowCustom && inputValue !== "" && !items.some(item_2 => item_2.label === inputValue || item_2.value === inputValue) ? {
      value: inputValue,
      label: inputValue
    } : undefined;
    T4 = Popover;
    t14 = open;
    t15 = setOpen;
    let t17;
    if ($[29] !== className) {
      t17 = cn("w-[200px] justify-between font-normal", className);
      $[29] = className;
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    let t18;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    const t19 = <Button id={id} variant="outline" role="combobox" aria-expanded={open} disabled={disabled} type="button" className={t17}><div className="flex min-w-0 flex-1 items-center gap-2">{selected.size === 1 ? items.find(item_3 => selected.has(item_3.value))?.icon : null}<span className="truncate">{selected.size === 0 ? placeholder : selected.size === 1 ? items.find(item_4 => selected.has(item_4.value))?.label ?? [...selected][0] : "Multiple selected"}</span></div>{t18}</Button>;
    if ($[32] !== t19) {
      t16 = <PopoverTrigger asChild={true}>{t19}</PopoverTrigger>;
      $[32] = t19;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    T3 = PopoverContent;
    t12 = "start";
    if ($[34] !== popoverContentClassName) {
      t13 = cn("w-[var(--radix-popover-trigger-width)] p-0", popoverContentClassName);
      $[34] = popoverContentClassName;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    T2 = Command;
    t10 = false;
    if ($[36] !== inputValue || $[37] !== searchPlaceholder) {
      t11 = <CommandInput placeholder={searchPlaceholder} value={inputValue} onValueChange={setInputValue} />;
      $[36] = inputValue;
      $[37] = searchPlaceholder;
      $[38] = t11;
    } else {
      t11 = $[38];
    }
    T1 = CommandList;
    if ($[39] !== allowCustom || $[40] !== emptyMessage) {
      t9 = !allowCustom && <CommandEmpty>{emptyMessage}</CommandEmpty>;
      $[39] = allowCustom;
      $[40] = emptyMessage;
      $[41] = t9;
    } else {
      t9 = $[41];
    }
    T0 = CommandGroup;
    t7 = customItem !== undefined && <ComboboxListItem key={`custom-${customItem.value}`} item={customItem} isSelected={selected.has(customItem.value)} onSelect={handleSelect} />;
    let t20;
    if ($[42] !== handleSelect || $[43] !== selected) {
      t20 = item_5 => <ComboboxListItem key={item_5.value} item={item_5} isSelected={selected.has(item_5.value)} onSelect={handleSelect} />;
      $[42] = handleSelect;
      $[43] = selected;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    t8 = orderedItems.map(t20);
    $[0] = allowCustom;
    $[1] = allowDeselect;
    $[2] = className;
    $[3] = disabled;
    $[4] = emptyMessage;
    $[5] = id;
    $[6] = inputValue;
    $[7] = items;
    $[8] = onValueChange;
    $[9] = open;
    $[10] = placeholder;
    $[11] = popoverContentClassName;
    $[12] = searchPlaceholder;
    $[13] = value;
    $[14] = T0;
    $[15] = T1;
    $[16] = T2;
    $[17] = T3;
    $[18] = T4;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    T0 = $[14];
    T1 = $[15];
    T2 = $[16];
    T3 = $[17];
    T4 = $[18];
    t10 = $[19];
    t11 = $[20];
    t12 = $[21];
    t13 = $[22];
    t14 = $[23];
    t15 = $[24];
    t16 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  let t17;
  if ($[45] !== T0 || $[46] !== t7 || $[47] !== t8) {
    t17 = <T0>{t7}{t8}</T0>;
    $[45] = T0;
    $[46] = t7;
    $[47] = t8;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== T1 || $[50] !== t17 || $[51] !== t9) {
    t18 = <T1>{t9}{t17}</T1>;
    $[49] = T1;
    $[50] = t17;
    $[51] = t9;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== T2 || $[54] !== t10 || $[55] !== t11 || $[56] !== t18) {
    t19 = <T2 shouldFilter={t10}>{t11}{t18}</T2>;
    $[53] = T2;
    $[54] = t10;
    $[55] = t11;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== T3 || $[59] !== t12 || $[60] !== t13 || $[61] !== t19) {
    t20 = <T3 align={t12} className={t13}>{t19}</T3>;
    $[58] = T3;
    $[59] = t12;
    $[60] = t13;
    $[61] = t19;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== T4 || $[64] !== t14 || $[65] !== t15 || $[66] !== t16 || $[67] !== t20) {
    t21 = <T4 open={t14} onOpenChange={t15}>{t16}{t20}</T4>;
    $[63] = T4;
    $[64] = t14;
    $[65] = t15;
    $[66] = t16;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  return t21;
}
interface ComboboxListItemProps {
  item: ComboboxItem;
  isSelected: boolean;
  onSelect: (value: string) => void;
}
function ComboboxListItem(t0) {
  const $ = _c(21);
  const {
    item,
    isSelected,
    onSelect
  } = t0;
  const t1 = item.value;
  const t2 = item.value;
  let t3;
  if ($[0] !== item.value || $[1] !== onSelect) {
    t3 = () => {
      onSelect(item.value);
    };
    $[0] = item.value;
    $[1] = onSelect;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = item.isGroupHeader === true && "border-t";
  let t5;
  if ($[3] !== t4) {
    t5 = cn(t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isSelected || $[6] !== item.icon) {
    t6 = isSelected ? <Check className="mr-2 h-4 w-4" /> : item.icon === undefined ? <div className="mr-2 h-4 w-4" /> : item.icon;
    $[5] = isSelected;
    $[6] = item.icon;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== item.label) {
    t7 = <span className="block whitespace-pre-wrap">{item.label}</span>;
    $[8] = item.label;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== item.description) {
    t8 = item.description !== undefined && <span className="text-muted-foreground block break-all font-mono text-xs">{item.description}</span>;
    $[10] = item.description;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <span className="min-w-0 flex-1">{t7}{t8}</span>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== item.value || $[16] !== t3 || $[17] !== t5 || $[18] !== t6 || $[19] !== t9) {
    t10 = <CommandItem key={t1} value={t2} onSelect={t3} className={t5}>{t6}{t9}</CommandItem>;
    $[15] = item.value;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}